import jsPDF from 'jspdf';
import { dessinerEnTete, dessinerPied, versRgb } from './planningPdfCommun';

/**
 * LE PDF DU PLANNING ORAUX — grille ou ordre de passage (lot PO.4, D27, maquette révision 14).
 *
 * L'APERÇU DE L'ÉCRAN EST CE DOCUMENT : l'onglet « Documents & diffusion » affiche le blob que produit cette fonction,
 * et « Télécharger le PDF » enregistre le même. Rien n'est écrit sur le serveur (nature B, documents.md §2) : un
 * modèle d'export garde les RÉGLAGES, le fichier se refait à la demande avec le planning du moment.
 *
 * ── CE QUE LE PAPIER MONTRE (spec §9.1, D11, D27) ───────────────────────────
 *   · en-tête COMMUN au Planning annuel (logos université + département, titre centré) ; dessous, les formations,
 *     puis — chacune si cochée — la ligne du découpage horaire et (BUT1) celle des évaluations ;
 *   · au moins UNE PAGE PAR JOURNÉE ; en BUT1 « grille », une page par SALLE et par journée ; chaque page porte un
 *     bandeau (« Samedi 13 juin 2026 · 12 passages ») ;
 *   · SEULS LES CRÉNEAUX OCCUPÉS sont imprimés ; une LIGNE N'EST JAMAIS COUPÉE : elle passe entière à la page
 *     suivante, qui rappelle le bandeau « (suite) » et l'en-tête du tableau ;
 *   · le jury en toutes lettres, membres extérieurs compris (ligne orange) ; en BUT1, une colonne par évaluation
 *     COCHÉE, et un jury d'oral identique sur toute la page écrit une seule fois, dans le bandeau ;
 *   · le TRAIT sous l'en-tête et la barre des bandeaux : la COULEUR DU DÉPARTEMENT (Administration → En-tête des
 *     documents, 2026-10-16 ; l'orange d'origine à défaut) ;
 *   · JAMAIS DE PHOTO (D6) ; pied commun « Généré le … par … · p. X/N » (PO-E22).
 *
 * La pagination est calculée AVANT de dessiner (le pied dit « p. 1/3 » dès la première page, l'écran annonce le
 * nombre de pages) — même règle que planningPdfEvenements.js.
 */

const FORMATS = { portrait: { largeur: 210, hauteur: 297 }, paysage: { largeur: 297, hauteur: 210 } };
const MARGE_X = 10;
const BAS_MARGE = 13;            // au-dessus du pied
const TAILLE = 7.5;
const INTERLIGNE = 3.2;
const PAD = 1.3;                 // marge intérieure d'une cellule
const HAUT_BANDEAU = 6;
const HAUT_ENTETE_TABLEAU = 6.2;
const VIOLET = [109, 40, 217];
const ORANGE_FONCE = [154, 52, 18];

// ── Calculs partagés avec l'écran (une seule copie : l'écran les importe d'ici) ──────────────────
const min = (h) => { const m = /^(\d{1,2}):(\d{2})/.exec(String(h || '')); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * PO.2a — les débuts de passage d'une journée (les LIGNES de la grille), jusqu'à la fin réglée, sinon la fin au plus tard.
 * ⚠️ Même calcul que `lignesDuJour` du serveur (planningOrauxService) : l'un change, l'autre suit.
 */
export function lignesHoraires(p) {
    const act = (p.duree_presentation_min || 0) + (p.duree_questions_min || 0) + (p.duree_deliberation_min || 0);
    const pas = act + (p.duree_preparation_min || 0), fin = min(p.heure_fin || p.heure_fin_max), out = [];
    if (act <= 0 || fin === null) return out;
    let t = min(p.heure_debut), garde = 0;
    const pd = p.pause_debut ? min(p.pause_debut) : null, pf = p.pause_fin ? min(p.pause_fin) : null;
    while (t + act <= fin && garde++ < 500) {
        if (pd !== null && t < pf && t + act > pd) { t = pf; continue; }
        out.push(t); t += pas;
    }
    return out;
}

/** La valeur d'un champ de capsule (écran) ou de cellule (PDF). */
export function valeurChamp(c, k) {
    if (k === 'nom') return `${c.nom || ''} ${c.prenom || ''}`.trim();
    if (k === 'tuteur') return c.tuteur ? `${c.tuteur.prenom || ''} ${(c.tuteur.nom || '').toUpperCase()}`.trim() : '';
    return c[k] || '';
}

const nomLong = (e) => (e ? `${String(e.nom || '').toUpperCase()} ${e.prenom || ''}`.trim() : '');

/** « samedi 13 juin 2026 » → « Samedi 13 juin 2026 » */
export function jourPdf(iso) {
    const t = new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Le texte « Évaluations : … » sous le titre (BUT1), d'après les évaluations retenues. */
export function texteEvaluations(evals) {
    return evals.length ? 'Évaluations : ' + evals.map((r) => `${r.libelle} (${r.presence ? "à l'oral" : 'sur le rapport'})`).join(' · ') : '';
}

/** Le texte du découpage horaire sous le titre, d'après les durées du planning. */
export function texteDecoupage(p) {
    const parts = [];
    if (p.duree_presentation_min) parts.push(`${p.duree_presentation_min} min de présentation`);
    if (p.duree_questions_min) parts.push(`${p.duree_questions_min} min de questions`);
    if (p.duree_deliberation_min) parts.push(`${p.duree_deliberation_min} min de délibération`);
    if (p.duree_preparation_min) parts.push(`${p.duree_preparation_min} min de préparation`);
    return parts.join(' · ');
}

/** Les journées et les évaluations RETENUES : tout sauf ce qui est décoché (un ajout ultérieur est imprimé). */
export function joursRetenus(p, o) { return (p.journees || []).filter((j) => !(o.jours_exclus || []).includes(j)); }
export function evalsRetenues(p, o) {
    return p.modele === 'but1' ? (p.config?.roles || []).filter((r) => !(o.evals_exclues || []).includes(r.cle)) : [];
}

/** Le nom du fichier : le titre, et la journée quand une seule est imprimée (révision 14). */
export function nomFichier(p, o) {
    const jr = joursRetenus(p, o);
    const base = (p.titre || 'Oraux') + ((p.journees || []).length > 1 && jr.length === 1 ? ' — ' + jourPdf(jr[0]) : '');
    return base.replace(/[\\/:*?"<>|]+/g, '-').trim() + '.pdf';
}

// ── Les jurys d'un passage (mêmes règles que l'écran : propre au passage, sinon celui de la salle) ──
function lecteurJurys(p) {
    // chaque famille par défaut : un planning peut n'en porter qu'une partie (défaut trouvé au banc des convocations, 2026-10-16)
    const J = { colonnes: {}, passages: {}, externes: {}, roles: {}, ...(p.jurys || {}) };
    const JR = { colonnes: {}, passages: {}, ...(J.roles || {}) };
    const N = p.nb_jurys || 1;
    const ens = new Map((p.enseignants || []).map((e) => [e.id, e]));
    const noms = (ids) => ids.filter(Boolean).map((id) => nomLong(ens.get(id))).filter(Boolean);
    return {
        standard: (c) => {
            const propre = (J.passages[c.id] || []).slice(0, N);
            return noms(propre.some(Boolean) ? propre : (J.colonnes[c.colonne_id] || []).slice(0, N));
        },
        externes: (c) => (J.externes?.[c.id] || []).map((x) => (x.qualite ? `${x.nom} (${x.qualite})` : x.nom)),
        evaluation: (c, r) => {
            const nb = r.presence ? (r.nb || 1) : 1;
            const propre = ((JR.passages?.[c.id] || {})[r.cle] || []).slice(0, nb);
            if (!r.presence || propre.some(Boolean)) return noms(propre);
            return noms(((JR.colonnes?.[c.colonne_id] || {})[r.cle] || []).slice(0, nb));
        }
    };
}

/** Le jury d'un passage en toutes lettres (convocation de l'étudiant, D15/D28) : Standard → enseignants + extérieurs ;
 *  BUT1 → le jury d'ORAL seulement (les correcteurs du rapport ne sont pas « son jury »). */
export function juryDuPassage(planning, c) {
    const L = lecteurJurys(planning);
    if (planning.modele === 'but1') {
        return [...new Set((planning.config?.roles || []).filter((r) => r.presence).flatMap((r) => L.evaluation(c, r)))];
    }
    return [...L.standard(c), ...L.externes(c)];
}

/**
 * Fabrique le PDF.
 * @param {Object} p.planning   le planning tel que `GET /api/planning-oraux/:id` le renvoie
 * @param {string} p.type       'grille' | 'ordre'
 * @param {Object} p.options    { orientation, champs: {origine: [clés]}, jury, horaires, logos, pied, decoupage,
 *                                decoupage_texte, evaluations, evaluations_texte, jours_exclus, evals_exclues }
 * @returns {{ pdf, pages, passages, journees }}
 */
export function genererPdfOraux({ planning: p, type = 'grille', options: o = {}, auteur = '', logo = null, logoUniversite = null, couleurTrait = null }) {
    const rgb = versRgb(couleurTrait || '#ea580c'), TRAIT = [rgb.r, rgb.g, rgb.b];
    const orientation = o.orientation === 'portrait' ? 'portrait' : 'paysage';
    const F = FORMATS[orientation];
    const pdf = new jsPDF({ orientation: orientation === 'paysage' ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
    const but1 = p.modele === 'but1';
    const lignes = lignesHoraires(p);
    const act = (p.duree_presentation_min || 0) + (p.duree_questions_min || 0) + (p.duree_deliberation_min || 0);
    const cols = (p.colonnes || []).filter((c) => c.salle);
    const posCol = new Map(cols.map((c, i) => [c.id, i]));
    const jours = joursRetenus(p, o);
    const evals = evalsRetenues(p, o);
    const jury = but1 ? evals.length > 0 : o.jury !== false;
    const horaires = o.horaires !== false;
    const L = lecteurJurys(p);
    const largeurUtile = F.largeur - MARGE_X * 2;

    // ── l'en-tête : titre, formations, découpage, évaluations ──
    const sous = [(p.formations || []).map((f) => f.code).join(' · ')];
    const lignesSup = [];
    if (o.decoupage !== false) { const t = o.decoupage_texte || texteDecoupage(p); if (t) lignesSup.push(t); }
    if (but1 && o.evaluations !== false && evals.length) { const t = o.evaluations_texte || texteEvaluations(evals); if (t) lignesSup.push(t); }
    const HAUT_CONTENU = 19 + lignesSup.length * 3.6;
    const yPied = F.hauteur - 7;
    const basPage = F.hauteur - BAS_MARGE;

    // ── le contenu d'une cellule « candidat » ──
    const champsDe = (c) => (o.champs?.[c.origine] || ['nom']).filter((k) => k !== 'photo');
    const textesCandidat = (c) => champsDe(c).map((k) => valeurChamp(c, k)).filter(Boolean);
    const horaire = (c) => (lignes[c.ligne] !== undefined ? [hhmm(lignes[c.ligne]), hhmm(lignes[c.ligne] + act)] : ['—', '']);

    // Mesure : un « bloc » de cellule = des lignes [texte, style] ; style = 'gras' | 'normal' | 'jury' | 'ext' | 'gris'
    const decouper = (texte, largeur, style) => {
        pdf.setFont('helvetica', style === 'gras' ? 'bold' : 'normal');
        pdf.setFontSize(TAILLE);
        return pdf.splitTextToSize(String(texte), Math.max(4, largeur - 2 * PAD)).map((t) => [t, style]);
    };
    const hauteurBloc = (bloc) => Math.max(1, bloc.length) * INTERLIGNE + 2 * PAD;

    const celluleCandidat = (c, largeur, avecJury) => {
        const t = textesCandidat(c), bloc = [];
        t.forEach((x, i) => bloc.push(...decouper(x, largeur, i === 0 ? 'gras' : 'normal')));
        if (avecJury) {
            const n = L.standard(c);
            if (n.length) bloc.push(...decouper('Jury : ' + n.join(', '), largeur, 'jury'));
            const e = L.externes(c);
            if (e.length) bloc.push(...decouper('+ ' + e.join(', '), largeur, 'ext'));
        }
        return bloc;
    };

    // ── les SECTIONS : une par journée (ou journée × salle en BUT1 grille) ; chacune = bandeau + en-tête + rangées ──
    const sections = [];
    const placesDu = (jour) => (p.candidats || []).filter((c) => c.place && c.jour === jour && posCol.has(c.colonne_id))
        .sort((a, b) => a.ligne - b.ligne || posCol.get(a.colonne_id) - posCol.get(b.colonne_id));
    let total = 0;

    for (const jour of jours) {
        const places = placesDu(jour);
        if (!places.length) continue;
        total += places.length;
        if (type === 'ordre') {
            const lc = [8, 21, 0, 18, jury ? 0 : null].filter((x) => x !== null);
            const libres = lc.filter((x) => x === 0).length, fixe = lc.reduce((s, x) => s + x, 0);
            const flex = (largeurUtile - fixe) / libres;
            const largeurs = lc.map((x) => x || flex);
            const entetes = ['#', 'Heure', 'Candidat', 'Salle'].concat(jury ? [but1 ? 'Évaluateurs' : 'Jury'] : []);
            const rangees = places.map((c, i) => {
                const [d, f] = horaire(c);
                const cellules = [
                    decouper(String(i + 1), largeurs[0], 'gris'),
                    decouper(horaires ? `${d}–${f}` : d, largeurs[1], 'normal'),
                    decouper(textesCandidat(c).join(' · '), largeurs[2], 'normal'),
                    decouper(cols[posCol.get(c.colonne_id)].salle.code + (c.activite ? ` (${c.activite})` : ''), largeurs[3], 'normal')
                ];
                if (jury) {
                    if (but1) cellules.push(evals.flatMap((r) => decouper(`${r.libelle} : ${L.evaluation(c, r).join(', ') || '—'}`, largeurs[4], 'normal')));
                    else {
                        const b = [];
                        const n = L.standard(c); if (n.length) b.push(...decouper(n.join(', '), largeurs[4], 'normal'));
                        const e = L.externes(c); if (e.length) b.push(...decouper('+ ' + e.join(', '), largeurs[4], 'ext'));
                        cellules.push(b);
                    }
                }
                return { cellules, hauteur: Math.max(...cellules.map(hauteurBloc)) };
            });
            sections.push({ bandeau: jourPdf(jour), droite: `${places.length} passage${places.length > 1 ? 's' : ''}`, largeurs, entetes, rangees });
        } else if (but1) {
            for (const col of cols) {
                const deCol = places.filter((c) => c.colonne_id === col.id);
                if (!deCol.length) continue;
                // un jury d'oral IDENTIQUE sur toute la salle ce jour-là s'écrit une fois, dans le bandeau
                const communs = evals.filter((r) => {
                    if (!r.presence) return false;
                    const v = L.evaluation(deCol[0], r).join(', ');
                    return v && deCol.every((c) => L.evaluation(c, r).join(', ') === v);
                });
                const colonnesEval = evals.filter((r) => !communs.includes(r));
                const lHeure = horaires ? 22 : 13;
                const lEtu = colonnesEval.length ? Math.min(70, (largeurUtile - lHeure) * 0.34) : largeurUtile - lHeure;
                const lEval = colonnesEval.length ? (largeurUtile - lHeure - lEtu) / colonnesEval.length : 0;
                const largeurs = [lHeure, lEtu, ...colonnesEval.map(() => lEval)];
                const entetes = ['Heure', 'Étudiant', ...colonnesEval.map((r) => [r.libelle, r.presence ? "à l'oral" : 'sur le rapport'])];
                const rangees = deCol.map((c) => {
                    const [d, f] = horaire(c);
                    const cellules = [decouper(horaires ? `${d}–${f}` : d, lHeure, 'normal'), celluleCandidat(c, lEtu, false),
                        ...colonnesEval.map((r) => decouper(L.evaluation(c, r).join(', ') || '—', lEval, 'normal'))];
                    return { cellules, hauteur: Math.max(...cellules.map(hauteurBloc)) };
                });
                const oral = communs.map((r) => `${r.libelle} : ${L.evaluation(deCol[0], r).join(', ')}`).join(' · ');
                sections.push({ bandeau: `${jourPdf(jour)} — Salle ${col.salle.code}`, droite: (oral ? oral + ' · ' : '') + `${deCol.length} étudiant${deCol.length > 1 ? 's' : ''}`, largeurs, entetes, rangees });
            }
        } else {
            const lHeure = 13, lSalle = (largeurUtile - lHeure) / Math.max(1, cols.length);
            const largeurs = [lHeure, ...cols.map(() => lSalle)];
            // D31 : « salle — activité » en tête de colonne (modèle « Plusieurs oraux le même jour »)
            const entetes = ['Heure', ...cols.map((c) => c.salle.code + (c.activite ? ` — ${c.activite}` : ''))];
            const parLigne = new Map();
            for (const c of places) { if (!parLigne.has(c.ligne)) parLigne.set(c.ligne, []); parLigne.get(c.ligne).push(c); }
            const rangees = [...parLigne.keys()].sort((a, b) => a - b).map((l) => {
                const deb = lignes[l];
                const heure = deb === undefined ? [['—', 'gris']] : [[hhmm(deb), 'gras']].concat(horaires ? [[hhmm(deb + act), 'gris']] : []);
                const cellules = [heure, ...cols.map((col) => {
                    const c = parLigne.get(l).find((x) => x.colonne_id === col.id);
                    return c ? celluleCandidat(c, lSalle, jury) : [];
                })];
                return { cellules, hauteur: Math.max(...cellules.map(hauteurBloc)) };
            });
            sections.push({ bandeau: jourPdf(jour), droite: `${places.length} passage${places.length > 1 ? 's' : ''}`, largeurs, entetes, rangees });
        }
    }

    // ── LA PAGINATION : chaque section commence une page ; une rangée qui ne tient pas passe entière à la suivante ──
    const hauteurEntetes = (s) => {
        const deuxLignes = s.entetes.some((e) => Array.isArray(e));
        return deuxLignes ? HAUT_ENTETE_TABLEAU + 2.6 : HAUT_ENTETE_TABLEAU;
    };
    const pages = [];
    for (const s of sections) {
        const tete = HAUT_BANDEAU + 1 + hauteurEntetes(s);
        let page = { section: s, suite: false, rangees: [] }, y = HAUT_CONTENU + tete;
        for (const r of s.rangees) {
            if (page.rangees.length && y + r.hauteur > basPage) {
                pages.push(page);
                page = { section: s, suite: true, rangees: [] }; y = HAUT_CONTENU + tete;
            }
            page.rangees.push(r); y += r.hauteur;
        }
        pages.push(page);
    }
    const message = !jours.length ? 'Aucune journée cochée : cochez au moins une journée.'
        : 'Aucun passage placé' + (jours.length < (p.journees || []).length ? ' ces journées-là' : '') + ' : le PDF n\'imprime que les créneaux occupés.';
    const nbPages = Math.max(1, pages.length);

    /* ── DESSIN ───────────────────────────────────────────────────────────── */
    const enTete = () => {
        dessinerEnTete(pdf, { largeurPage: F.largeur, margeX: MARGE_X, logo: o.logos === false ? null : logo,
            logoUniversite: o.logos === false ? null : logoUniversite, titre: p.titre, titreParDefaut: 'Oraux', sousTitre: sous[0] });
        pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 116, 139);
        lignesSup.forEach((t, i) => pdf.text(pdf.splitTextToSize(t, F.largeur - 90)[0], F.largeur / 2, 17.6 + i * 3.6, { align: 'center' }));
        pdf.setDrawColor(...TRAIT); pdf.setLineWidth(0.5);
        pdf.line(MARGE_X, HAUT_CONTENU - 2.5, F.largeur - MARGE_X, HAUT_CONTENU - 2.5);
    };
    const couleur = (style) => {
        if (style === 'jury') { pdf.setTextColor(...VIOLET); pdf.setFont('helvetica', 'normal'); }
        else if (style === 'ext') { pdf.setTextColor(...ORANGE_FONCE); pdf.setFont('helvetica', 'normal'); }
        else if (style === 'gris') { pdf.setTextColor(100, 116, 139); pdf.setFont('helvetica', 'normal'); }
        else { pdf.setTextColor(30, 41, 59); pdf.setFont('helvetica', style === 'gras' ? 'bold' : 'normal'); }
    };
    const dessinerPage = (pg) => {
        const s = pg.section;
        let y = HAUT_CONTENU;
        // bandeau
        pdf.setFillColor(241, 245, 249); pdf.rect(MARGE_X, y, largeurUtile, HAUT_BANDEAU, 'F');
        pdf.setFillColor(...TRAIT); pdf.rect(MARGE_X, y, 1, HAUT_BANDEAU, 'F');
        pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(15, 23, 42);
        pdf.text(s.bandeau + (pg.suite ? ' (suite)' : ''), MARGE_X + 3, y + 4.1);
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 116, 139); pdf.setFontSize(7.5);
        pdf.text(pdf.splitTextToSize(s.droite, largeurUtile * 0.55)[0], MARGE_X + largeurUtile - 2, y + 4.1, { align: 'right' });
        y += HAUT_BANDEAU + 1;
        // en-tête du tableau
        const hE = hauteurEntetes(s);
        pdf.setFillColor(241, 245, 249); pdf.rect(MARGE_X, y, largeurUtile, hE, 'F');
        let x = MARGE_X;
        s.entetes.forEach((e, i) => {
            const [t, st] = Array.isArray(e) ? e : [e, null];
            pdf.setFontSize(7); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(51, 65, 85);
            pdf.text(pdf.splitTextToSize(String(t).toUpperCase(), s.largeurs[i] - 2 * PAD)[0], x + PAD, y + 4);
            if (st) { pdf.setFontSize(6); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 116, 139); pdf.text(st, x + PAD, y + 6.8); }
            x += s.largeurs[i];
        });
        pdf.setDrawColor(203, 213, 225); pdf.setLineWidth(0.15);
        pdf.rect(MARGE_X, y, largeurUtile, hE);
        y += hE;
        // rangées (jamais coupées : la pagination l'a garanti)
        for (const r of pg.rangees) {
            let xc = MARGE_X;
            for (let i = 0; i < r.cellules.length; i++) {
                pdf.setFontSize(TAILLE);
                for (let k = 0; k < r.cellules[i].length; k++) {
                    const [t, st] = r.cellules[i][k];
                    couleur(st); pdf.text(t, xc + PAD, y + PAD + 2.4 + k * INTERLIGNE);
                }
                pdf.setDrawColor(203, 213, 225); pdf.rect(xc, y, s.largeurs[i], r.hauteur);
                xc += s.largeurs[i];
            }
            y += r.hauteur;
        }
    };

    if (!pages.length) {
        enTete();
        pdf.setFontSize(9.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(148, 163, 184);
        pdf.text(message, F.largeur / 2, HAUT_CONTENU + 12, { align: 'center' });
        if (o.pied !== false) dessinerPied(pdf, { largeurPage: F.largeur, margeX: MARGE_X, y: yPied, page: 1, pages: 1, auteur });
    }
    pages.forEach((pg, i) => {
        if (i > 0) pdf.addPage();
        enTete();
        dessinerPage(pg);
        if (o.pied !== false) dessinerPied(pdf, { largeurPage: F.largeur, margeX: MARGE_X, y: yPied, page: i + 1, pages: nbPages, auteur });
    });

    return { pdf, pages: nbPages, passages: total, journees: jours.length };
}
